import Box from "@mui/material/Box";
import { LIST_GRID_EXPANDED_INSET } from "@/components/list-grid/ListGridTemplate";
import type { Inventory } from "@/api/inventory/_models/Inventory";
import { ListGridExpandedFields } from "@/components/list-grid/ListGridExpandedFields";
import type { ListGridExpandedField } from "@/components/list-grid/ListGridExpandedFields";
import { ListGridExpandedSection } from "@/components/list-grid/ListGridExpandedSection";
import { toCin7Level } from "@/features/inventory/inventory-list/inventoryList.utils";
import { InventoryListExpandedStock } from "./InventoryListExpandedStock";
import { InventoryListExpandedSummary } from "./InventoryListExpandedSummary";

interface InventoryListExpandedProps {
  row: Inventory;
}

const toCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
};

const toItemFieldList = (row: Inventory): ListGridExpandedField[] => {
  return [
    { label: "SKU", value: row.sku },
    { label: "Product", value: row.productName ?? "—", isWrapping: true },
    { label: "Location", value: row.locationName, isWrapping: true },
  ];
};

const toMovementFieldList = (row: Inventory): ListGridExpandedField[] => {
  const cin7 = toCin7Level(row);
  return [
    { label: "Allocated", value: toCount(cin7?.allocated) },
    { label: "On order", value: toCount(cin7?.onOrder) },
    { label: "In transit", value: toCount(cin7?.inTransit) },
  ];
};

/**
 * An opened inventory row: its status note, then the item, on hand (Cin7
 * against each 3PL) and Cin7's movement.
 */
export const InventoryListExpanded = (props: InventoryListExpandedProps) => {
  const { row } = props;
  const itemFields = toItemFieldList(row);
  const movementFields = toMovementFieldList(row);

  return (
    <Box
      sx={{
        columnGap: 6,
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "repeat(2, minmax(0, 1fr))",
          lg: "minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1fr)",
        },
        pb: 2.5,
        pl: LIST_GRID_EXPANDED_INSET,
        pr: 3,
        pt: 0.5,
        rowGap: 2,
        "& > .InventoryListExpanded-stock": {
          gridColumn: { md: "1 / -1", lg: "auto" },
        },
      }}
    >
      <InventoryListExpandedSummary row={row} />
      <ListGridExpandedSection title="Item">
        <ListGridExpandedFields fields={itemFields} />
      </ListGridExpandedSection>
      <InventoryListExpandedStock row={row} />
      <ListGridExpandedSection title="Cin7 movement">
        <ListGridExpandedFields fields={movementFields} />
      </ListGridExpandedSection>
    </Box>
  );
};
